import { useListContext, SimpleListLoading } from 'react-admin';
import { MissionLight } from '@wwf/sdk';
import { Card } from '@mui/material';
import { CalendarMode } from './MissionCalendar';
import { parseISO, subWeeks, addWeeks } from 'date-fns';
import { MissionCalendarMonthView } from './views/MissionCalendarMonthView';
import { MissionCalendarWeekView } from './views/MissionCalendarWeekView';
import { MissionCalendarDayView } from './views/MissionCalendarDayView';

interface MissionCalendarContentProps {
    mode: CalendarMode;
    setMode: (mode: CalendarMode) => void;
    setAnchorDate: (date: Date) => void;
    rangeStart: Date;
    rangeEnd: Date;
}

export const MissionCalendarContent = ({
    mode,
    setMode,
    setAnchorDate,
    rangeStart,
    rangeEnd,
}: MissionCalendarContentProps) => {
    const { data: missions, isPending, error } = useListContext<MissionLight>();

    if (isPending) {
        return <SimpleListLoading hasLeftAvatarOrIcon hasSecondaryText />;
    }
    if (error) {
        return null;
    }

    // Expand the in-memory range slightly to match the server fetch
    // so padding days in the Month view grid are populated.
    const effectiveRangeStart = subWeeks(rangeStart, 1);
    const effectiveRangeEnd = addWeeks(rangeEnd, 1);

    // Filter overlapping missions in memory
    // mission overlaps if: start <= rangeEnd AND (end || start) >= rangeStart
    const overlappingMissions = (missions || []).filter(m => {
        if (!m.startDate) return false;

        const mStart = parseISO(m.startDate);
        const mEnd = m.endDate ? parseISO(m.endDate) : mStart;

        // start is before or on rangeEnd
        const startsBeforeOrIn = mStart <= effectiveRangeEnd;
        // end is after or on rangeStart
        const endsAfterOrIn = mEnd >= effectiveRangeStart;

        return startsBeforeOrIn && endsAfterOrIn;
    });

    return (
        <Card sx={{ width: '100%', minHeight: 600, overflow: 'hidden' }}>
            {mode === 'month' && (
                <MissionCalendarMonthView
                    missions={overlappingMissions}
                    rangeStart={rangeStart}
                    rangeEnd={rangeEnd}
                    setMode={setMode}
                    setAnchorDate={setAnchorDate}
                />
            )}
            {mode === 'week' && (
                <MissionCalendarWeekView
                    missions={overlappingMissions}
                    rangeStart={rangeStart}
                    rangeEnd={rangeEnd}
                    setMode={setMode}
                    setAnchorDate={setAnchorDate}
                />
            )}
            {mode === 'day' && (
                <MissionCalendarDayView
                    missions={overlappingMissions}
                    rangeStart={rangeStart}
                    rangeEnd={rangeEnd}
                />
            )}
        </Card>
    );
};
